import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Eye, EyeOff, ShieldCheck, GraduationCap, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useBranding } from "@/lib/branding";

const UD_SEAL = "https://customer-assets-wrfwihn1.emergentagent.net/job_alumni-hub-101/artifacts/n52mfin2_image.png";

export default function ClaimProfile() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUserSession } = useAuth();
  const b = useBranding();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/claim/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(apiError(e.response?.data?.detail) || "Invalid link"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/claim/${token}`, { password: pw });
      localStorage.setItem("alumnios_token", data.token);
      setUserSession(data.user);
      localStorage.setItem("ud_alumni_last_tab", "/me");
      toast.success("Profile claimed — welcome!");
      navigate("/me");
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card rounded-xl shadow-xl border border-border overflow-hidden fade-up">
        <div className="bg-secondary px-6 py-6 flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center p-1">
            {b.logo_primary ? <img src={b.logo_primary} alt="seal" className="h-full w-full object-contain" /> : null}
          </div>
          <div className="text-white">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-accent">{b.university_name}</div>
            <div className="font-serif text-xl">Claim Your Profile</div>
          </div>
        </div>

        <div className="p-6">
          {err && !info && <div className="text-sm bg-destructive/10 text-destructive rounded-md px-3 py-2" data-testid="claim-error">{err}</div>}
          {!info && !err && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Verifying your link…</div>}

          {info && info.claimed && (
            <div className="text-center py-6">
              <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
              <p className="mt-3 text-sm text-muted-foreground">This profile has already been claimed.</p>
              <button data-testid="claim-goto-login" onClick={() => navigate("/login")} className="mt-4 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90">Go to Sign in</button>
            </div>
          )}

          {info && !info.claimed && (
            <form onSubmit={submit} data-testid="claim-form">
              <div className="flex items-center gap-2 text-sm text-primary mb-1"><GraduationCap className="h-4 w-4" /> Class of {info.class_year ?? "—"}</div>
              <h2 className="font-serif text-2xl tracking-tight">Welcome, {(info.preferred_name || info.first_name)} {info.last_name}</h2>
              <p className="text-sm text-muted-foreground mt-1 mb-5">Confirm this is you and create a password. You'll sign in with <b>{info.email || "your email"}</b>.</p>

              {err && (
                <div className="mb-4 text-sm bg-destructive/10 text-destructive rounded-md px-3 py-2" data-testid="claim-form-error">
                  {err}
                  {/already exists|already been claimed|please sign in/i.test(err) && (
                    <button type="button" data-testid="claim-error-signin" onClick={() => navigate("/login")} className="mt-2 block w-full py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">
                      Go to Sign in
                    </button>
                  )}
                </div>
              )}

              <label className="block text-sm font-medium mb-1">Create password</label>
              <div className="relative mb-4">
                <input data-testid="claim-password" type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} required placeholder="At least 8 characters"
                  className="w-full px-3 py-2.5 pr-10 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />
                <button type="button" data-testid="claim-toggle-pw" onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <label className="block text-sm font-medium mb-1">Confirm password</label>
              <input data-testid="claim-password2" type={show ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)} required
                className="w-full mb-5 px-3 py-2.5 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none" />

              <button data-testid="claim-submit" type="submit" disabled={busy} className="w-full py-2.5 rounded-md bg-primary text-white font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">
                {busy ? "Claiming…" : "Claim Profile & Continue"}
              </button>
              <div className="mt-4 flex items-center gap-1.5 text-[11px] text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Your information is private and secured.</div>
              <p className="mt-2 text-[11px] text-muted-foreground">
                By claiming your profile you agree to our{" "}
                {b.privacy_url ? (
                  <a data-testid="claim-privacy-link" href={b.privacy_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Notice</a>
                ) : (
                  <a data-testid="claim-privacy-link" href="/privacy" className="text-primary hover:underline">Privacy Notice</a>
                )}
                . We never sell or share your information.
              </p>
              <div className="mt-3 text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <button type="button" data-testid="claim-signin-link" onClick={() => navigate("/login")} className="text-primary font-medium hover:underline">Sign in</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
